import React, { useEffect, useState } from "react";
import "./AlertSettings.scss";
import { useGlobalSettings } from "../../../context/GlobalSettingsContext";
import {
  updateAlertSettings,
  getAllGlobalSettingsData,
  getRights
} from "../../../services/api/GlobalSettingsApi";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import JusticeLoader from "../../../components/JusticeLoader/JusticeLoader";

const AlertSettings = () => {
  const [fetchingData, setFetchingData] = useState(true);
  const { globalSettings, setGlobalSettings } = useGlobalSettings();
  const [settings, setSettings] = useState({
    allowWhatsAppAlerts: false,
    whatsAppAccessToken: "",
    whatsAppId: "",
    modules: []
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const allDisplayKeys = [
    { key: "cases", label: "Cases" },
    { key: "tasks", label: "Tasks" },
    { key: "expenses", label: "Expenses" },
  ];

  // Filter display keys based on fetched rights
  const [displayKeys, setDisplayKeys] = useState([]);

  // Function to mask sensitive data to approximately 100 characters
  const maskSensitiveData = (data) => {
    if (!data) return "";
    // Create a masked string of approximately 100 characters
    const maskedLength = 100;
    return "*".repeat(maskedLength);
  };
  

  useEffect(() => {
    const fetchAlertSettings = async () => {
      try {
        setFetchingData(true);
        
        // Fetch rights to filter modules
        const rightsResponse = await getRights();
        if (rightsResponse) {
          // Filter displayKeys to only show modules that are enabled in rights
          const filteredKeys = allDisplayKeys.filter(
            ({ key }) => rightsResponse.data.hasOwnProperty(key) && rightsResponse.data[key] === true
          );
          setDisplayKeys(filteredKeys);
        }

        // Fetch alert settings
        const response = await getAllGlobalSettingsData();

        if (response?.data?.result?.[0]?.alerts?.whatsApp) {
          const whatsAppSettings = response.data.result[0].alerts.whatsApp;
          setSettings({
            allowWhatsAppAlerts: whatsAppSettings.allowWhatsAppAlerts || false,
            whatsAppAccessToken: whatsAppSettings.whatsAppAccessToken || "",
            whatsAppId: whatsAppSettings.whatsAppId || "",
            modules: whatsAppSettings.modules || []
          });

          // Update global settings if not already set
          if (!globalSettings) {
            setGlobalSettings(response.data);
          }
        } else {
          // Set default values if no data exists
          setSettings({
            allowWhatsAppAlerts: false,
            whatsAppAccessToken: "",
            whatsAppId: "",
            modules: []
          });
        }
      } catch (error) {
        console.error("Error fetching alert settings:", error);
        showToast("Failed to fetch alert settings", "error");
        // Set defaults on error
        setSettings({
          allowWhatsAppAlerts: false,
          whatsAppAccessToken: "",
          whatsAppId: "",
          modules: []
        });
        setDisplayKeys([]);
      } finally {
        setFetchingData(false);
      }
    };

    fetchAlertSettings();
  }, []);

  useEffect(() => {
    if (globalSettings?.result?.[0]?.alerts?.whatsApp) {
      const whatsAppSettings = globalSettings.result[0].alerts.whatsApp;
      setSettings({
        allowWhatsAppAlerts: whatsAppSettings.allowWhatsAppAlerts || false,
        whatsAppAccessToken: whatsAppSettings.whatsAppAccessToken || "",
        whatsAppId: whatsAppSettings.whatsAppId || "",
        modules: whatsAppSettings.modules || []
      });
    }
  }, [globalSettings]);

  if (fetchingData) {
    return (
      <section className="alert-settings-section">
        <JusticeLoader message="Loading alert settings..." type="gavel" />
      </section>
    );
  }

  const handleToggle = () => {
    // Only allow toggle if credentials are present
    if (!settings.allowWhatsAppAlerts && (!settings.whatsAppAccessToken || !settings.whatsAppId)) {
      showToast("WhatsApp credentials are required to enable alerts. Please contact support.", "warning");
      return;
    }
    setSettings((prev) => ({ ...prev, allowWhatsAppAlerts: !prev.allowWhatsAppAlerts }));
  };

  const handleInputChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleModuleToggle = (moduleKey) => {
    setSettings((prev) => {
      const modules = prev.modules || [];
      const updatedModules = modules.includes(moduleKey)
        ? modules.filter((m) => m !== moduleKey)
        : [...modules, moduleKey];
      return { ...prev, modules: updatedModules };
    });
  };

  const isToggleEnabled = () => {
    return settings.whatsAppAccessToken && settings.whatsAppId;
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        whatsApp: {
          allowWhatsAppAlerts: settings.allowWhatsAppAlerts,
          whatsAppAccessToken: settings.whatsAppAccessToken,
          whatsAppId: settings.whatsAppId,
          modules: settings.modules
        }
      };

      await updateAlertSettings(payload);

      const updated = await getAllGlobalSettingsData();
      setGlobalSettings(updated.data);
      localStorage.setItem("globalSettings", JSON.stringify(updated.data));

      showToast("Alert settings updated successfully.", "success");
      setTimeout(() => {
        navigate("/home/displayboard");
      }, 300);
    } catch (err) {
      console.error(err);
      showToast("Something went wrong while updating alert settings!", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="alert-settings-section">
        <div className="settings-container">
          {/* WhatsApp Configuration Card */}
          <div className="settings-card">
            <h4 className="card-title">WhatsApp Alert Configuration</h4>

            
            <div className="toggle-row">
              <div className="toggle-label-wrapper">
                <label>Enable WhatsApp Alerts</label>
                {!isToggleEnabled() && (
                  <p className="helper-text">
                    Please provide WhatsApp Access Token and WhatsApp ID to enable alerts. Contact support for assistance.
                  </p>
                )}
              </div>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  checked={settings.allowWhatsAppAlerts || false}
                  onChange={handleToggle}
                  id="allowWhatsAppAlerts"
                  disabled={!isToggleEnabled()}
                />
                <label 
                  htmlFor="allowWhatsAppAlerts" 
                  className={`toggle-slider ${!isToggleEnabled() ? 'disabled' : ''}`}
                ></label>
              </div>
            </div>
            
            {settings.allowWhatsAppAlerts && (
              <>
                <div className="form-group">
                  <label>WhatsApp Access Token</label>
                  <input
                    type="text"
                    className="form-input masked-input"
                    value={settings.whatsAppAccessToken ? maskSensitiveData(settings.whatsAppAccessToken) : ""}
                    readOnly
                    placeholder="Contact support to update WhatsApp Access Token"
                  />
                </div>

                <div className="form-group">
                  <label>WhatsApp ID</label>
                  <input
                    type="text"
                    className="form-input masked-input"
                    value={settings.whatsAppId ? maskSensitiveData(settings.whatsAppId) : ""}
                    readOnly
                    placeholder="Contact support to update WhatsApp ID"
                  />
                </div>
              </>
            )}

          </div>

          {/* Module Selection Card */}
          {settings.allowWhatsAppAlerts && (
            <div className="settings-card">
              <h4 className="card-title">Alert Modules</h4>
              <p className="card-description">Select modules for which you want to receive WhatsApp alerts</p>
              
              {displayKeys.length > 0 ? (
                <div className="modules-grid">
                  {displayKeys.map(({ key, label }) => (
                    <div className="module-item" key={key}>
                      <label>{label}</label>
                      <div className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={settings.modules?.includes(key) || false}
                          onChange={() => handleModuleToggle(key)}
                          id={`module-${key}`}
                          disabled={!settings.allowWhatsAppAlerts}
                        />
                        <label 
                          htmlFor={`module-${key}`} 
                          className={`toggle-slider ${!settings.allowWhatsAppAlerts ? 'disabled' : ''}`}
                        ></label>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-rights-message">
                  No modules available for alerts based on your rights.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button
            className="cancel-btn"
            onClick={() => navigate("/home/displayboard")}
          >
            Cancel
          </button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Alert Settings"}
          </button>
        </div>
      </section>
    </>
  );
};

export default AlertSettings;
